import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import InquiryGuideCards from './components/InquiryGuideCards';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import PostSubmitGuide from './components/PostSubmitGuide';
import FAQSection from './components/FAQSection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';

function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full border border-accent-400/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 z-50 cursor-pointer"
      aria-label={t('contact.page.backToTop')}
    >
      <i className="ri-arrow-up-line text-base"></i>
    </button>
  );
}

export default function ContactPage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('contact.jsonld.breadcrumb1'),
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('contact.jsonld.breadcrumb2'),
        item: 'https://butler-academy.jp/contact',
      },
    ],
  };

  const orgData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: t('contact.jsonld.orgName'),
    parentOrganization: {
      '@type': 'Organization',
      name: t('contact.jsonld.parentOrg'),
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: t('contact.jsonld.addressLocality'),
    },
    telephone: '03-6824-4086',
  };

  if (!mounted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <Seo route="/contact" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgData) }}
      />

      <Header />
      <main>
        <HeroSection />

        <InquiryGuideCards />
        <ContactForm />
        <PostSubmitGuide />
        <ContactInfo />
        <FAQSection />
        <RelatedPages />

        <footer className="py-12 bg-background-100/50 text-center">
          <p className="text-[13px] text-foreground-400 tracking-[0.04em]">
            {t('contact.page.lastUpdated')}
          </p>
        </footer>

        <BackToTop />
      </main>
      <Footer />
    </>
  );
}
